import { useEffect, useState } from 'react';

import { homeForLanguage, pageForKnowledgeId } from './data';
import { ArrowIcon, ArrowUpRightIcon, ClockIcon, EyeIcon } from './icons';
import { PageHeaderImage } from './PageHeaderImage';

import { Link } from './router';
import type { Heading, SitePage, SitePageSummary } from './types';

const copy = {
  en: {
    home: 'Home', contents: 'On this page', read: 'min read', words: 'words', author: 'Author', educational: 'How to use this page', educationalBody: 'Clinical research, practical guidance, and lived experience are kept distinct in the text. Use the cited material to build questions for your own care.', sensitiveTitle: 'Sensitive article images are hidden', sensitiveBody: 'The 2024 journey includes nudity and graphic post-operative medical images. The complete written account remains visible.', showMedia: 'Show educational images', hideMedia: 'Hide images', relatedReading: 'Related reading', sourceNote: 'Links to outside sources open in a new tab. Full references, where used, remain in the article.', back: 'Back to the top',
  },
  es: {
    home: 'Inicio', contents: 'En esta página', read: 'min de lectura', words: 'palabras', author: 'Autor', educational: 'Cómo usar esta página', educationalBody: 'La investigación clínica, la orientación práctica y la experiencia vivida se mantienen diferenciadas. Usa el material citado para preparar preguntas sobre tu propia atención.', sensitiveTitle: 'Las imágenes sensibles del artículo están ocultas', sensitiveBody: 'El viaje de 2024 incluye desnudez e imágenes médicas postoperatorias explícitas. El relato escrito completo permanece visible.', showMedia: 'Mostrar imágenes educativas', hideMedia: 'Ocultar imágenes', relatedReading: 'Lecturas relacionadas', sourceNote: 'Los enlaces a fuentes externas se abren en otra pestaña. Las referencias completas, cuando se usan, permanecen en el artículo.', back: 'Volver arriba',
  },
};

function withoutSensitiveMedia(html: string) {
  return html
    .replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, '')
    .replace(/<picture\b[^>]*>[\s\S]*?<\/picture>/gi, '')
    .replace(/<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi, '')
    .replace(/<video\b[^>]*>[\s\S]*?<\/video>/gi, '')
    .replace(/<a\b[^>]*>\s*<img\b[^>]*>\s*<\/a>/gi, '')
    .replace(/<img\b[^>]*>/gi, '');
}

function useActiveHeading(headings: Heading[]) {
  const [activeId, setActiveId] = useState(headings[0]?.id ?? '');
  useEffect(() => {
    if (!headings.length || typeof IntersectionObserver === 'undefined') return;
    const elements = headings.map((heading) => document.getElementById(heading.id)).filter(Boolean) as HTMLElement[];
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]?.target.id) setActiveId(visible[0].target.id);
    }, { rootMargin: '-18% 0px -72% 0px', threshold: [0, 1] });
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [headings]);
  return activeId;
}

function TableOfContents({ page }: { page: SitePage }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const c = copy[language];
  const active = useActiveHeading(page.headings);
  if (!page.headings.length) return null;
  const navigation = () => (
    <nav aria-label={c.contents}>
      {page.headings.map((heading) => <a key={heading.id} href={`#${heading.id}`} className={`${heading.level === 3 ? 'toc-depth-3' : ''} ${active === heading.id ? 'is-active' : ''}`}>{heading.text}</a>)}
      <a className="toc-back" href="#main-content">{c.back}<span>↑</span></a>
    </nav>
  );
  return (
    <>
      <aside className="table-of-contents table-of-contents--desktop">
        <span>{c.contents}</span>
        {navigation()}
      </aside>
      <details className="table-of-contents table-of-contents--mobile">
        <summary>{c.contents}</summary>
        {navigation()}
      </details>
    </>
  );
}

function RelatedReading({ page, pages }: { page: SitePage; pages: SitePageSummary[] }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const c = copy[language];
  const byKnowledgeId = new Map(pages.filter((candidate) => candidate.lang === language).map((candidate) => [candidate.knowledgeId, candidate]));
  const related = (page.related ?? [])
    .map((id) => byKnowledgeId.get(id))
    .filter((candidate): candidate is SitePageSummary => Boolean(candidate && candidate.knowledgeId !== page.knowledgeId))
    .slice(0, 4);
  if (!related.length) return null;
  return (
    <aside className="related-reading">
      <h2>{c.relatedReading}</h2>
      <nav aria-label={c.relatedReading}>
        <ul>
          {related.map((item) => (
            <li key={item.id}><Link href={item.route}><strong>{item.title}</strong><span>{item.description}</span><ArrowIcon /></Link></li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}

export function ArticlePage({ page, pages }: { page: SitePage; pages: SitePageSummary[] }) {
  const language = page.lang === 'es' ? 'es' : 'en';
  const c = copy[language];
  const [showSensitiveMedia, setShowSensitiveMedia] = useState(false);
  useEffect(() => setShowSensitiveMedia(false), [page.id]);
  const collectionId = page.collection || page.category;
  const collection = pageForKnowledgeId(language, `collection-${collectionId}`);

  const articleHtml = page.sensitive && !showSensitiveMedia ? withoutSensitiveMedia(page.html) : page.html;

  return (
    <main id="main-content" className="article-page">
      <header className="article-hero">
        <div className="shell">
          <nav className="breadcrumbs" aria-label="Breadcrumb">
            <Link href={homeForLanguage(language).route}>{c.home}</Link><span>/</span>
            {collection ? <Link href={collection.route}>{collection.title}</Link> : <span>{collectionId}</span>}
          </nav>
          <div className="article-hero__grid">
            <div className="article-hero__copy"><span className="eyebrow">{collection?.title ?? (language === 'es' ? 'Artículo' : 'Article')}</span><h1>{page.title}</h1><p>{page.description}</p></div>
            <PageHeaderImage page={page} priority className="article-hero__art" />
            <dl className="article-meta"><div><dt><ClockIcon />{page.readMinutes} {c.read}</dt><dd>{page.wordCount.toLocaleString(language)} {c.words}</dd></div></dl>
          </div>
        </div>
      </header>

      <div className="shell article-layout">
        <article className="article-main">
          {page.sensitive && <aside className="media-consent"><EyeIcon /><div><strong>{c.sensitiveTitle}</strong><p>{c.sensitiveBody}</p></div><label className="media-switch"><input type="checkbox" role="switch" checked={showSensitiveMedia} onChange={(event) => setShowSensitiveMedia(event.target.checked)} aria-label={showSensitiveMedia ? c.hideMedia : c.showMedia} /><span className="media-switch__track" aria-hidden="true"><span /></span><span>{showSensitiveMedia ? c.hideMedia : c.showMedia}</span></label></aside>}
          {/* generate-knowledge-site.mjs sanitizes this HTML through a strict
              tag/attribute/scheme allowlist before it reaches React. */}
          <div className={`article-body ${page.sensitive && !showSensitiveMedia ? 'article-body--media-hidden' : ''}`} dangerouslySetInnerHTML={{ __html: articleHtml }} />
          <p className="external-note"><ArrowUpRightIcon />{c.sourceNote}</p>
          <RelatedReading page={page} pages={pages} />
        </article>
        <TableOfContents page={page} />
      </div>

    </main>
  );
}
